// frontend/src/components/InvoiceEdit.js
import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Descriptions, Typography, Divider, Select, Button, Table, Alert, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';

import { INVOICE } from '../Constants';
import { toLocalDateTimeString } from '../utils/dateUtils';
import ServiceHistoryTab from './ServiceHistoryTab';
import { useConstants } from '../context/ConstantsContext';
import { updateInvoice, fetchInvoice as fetchInvoiceData, fetchEquipment as fetchEquipmentData } from '../api';

const { TabPane } = Tabs;

const InvoiceEdit = ({ invoiceId, messageApi, onContractTypeChange }) => {
  const constants = useConstants();
  const [invoice, setInvoice] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingContractType, setEditingContractType] = useState(false);
  const [contractType, setContractType] = useState(null);
  const editingRef = useRef(null);

  useEffect(() => {
    // Handle clicks outside the edit UI
    const handleClickOutside = (event) => {
      // Check if the click is inside an Ant Design dropdown
      const isAntDropdown = event.target.closest('.ant-select-dropdown');
      
      if (editingRef.current && !editingRef.current.contains(event.target) && !isAntDropdown) {
        setEditingContractType(false); // Cancel editing
        setContractType(invoice?.contract_type); // Reset to original value
      }
    };

    if (editingContractType === true) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingContractType, invoice]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const data = await fetchInvoiceData(invoiceId);
      setInvoice(data);
      setContractType(data.contract_type);
    } catch (error) {
      messageApi.error('Failed to load invoice details.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const data = await fetchEquipmentData();
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
      setEquipment(sortedData);
    } catch (error) {
      messageApi.error('Failed to load available equipment.');
    } finally {
      setLoading(false);
    }
  };

  const calculateInvoiceSummary = () => {
    if (!invoice || !invoice.service_histories) return [];

    const minDuration = invoice.min_invoice_equipment_duration || 2.0;
    const equipmentSummary = {};
    
    invoice.service_histories.forEach(sh => {
      sh.equipment.forEach(equip => {
        // Skip equipment that has been removed from invoice (null invoice fields)
        if (equip.invoice_duration !== null && equip.invoice_quantity !== null) {
          const key = equip.name;
          if (!equipmentSummary[key]) {
            equipmentSummary[key] = {
              name: equip.name,
              rawDuration: 0,
              effectiveDuration: 0,
              quantity: 0,
              rate: equip.client_rate_cad || equip.yeti_client_rate || 0,
              total: 0,
              minDuration: minDuration
            };
          }
          
          const rawDuration = equip.invoice_duration;
          // Use effective_duration from API if available, otherwise calculate it
          const effectiveDuration = equip.effective_duration || Math.max(minDuration, rawDuration);
          const quantity = equip.invoice_quantity;
          const rate = equip.client_rate_cad || equip.yeti_client_rate || 0;
          
          equipmentSummary[key].rawDuration += rawDuration;
          equipmentSummary[key].effectiveDuration += effectiveDuration;
          equipmentSummary[key].quantity += quantity;
          // Use effective duration for total calculation (matches backend calculation)
          equipmentSummary[key].total += effectiveDuration * rate * quantity;
        }
      });
    });

    return Object.values(equipmentSummary);
  };

  const invoiceSummaryColumns = [
    {
      title: 'Equipment',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: (
        <span>
          Total Duration (hrs){' '}
          <Tooltip title="Total duration across all service histories for this equipment, with minimum duration applied per service history">
            <InfoCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'duration',
      render: (_, record) => (
        <Tooltip 
          title={
            <div>
              <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>Duration Breakdown:</div>
              {invoice?.service_histories?.map((sh, idx) => {
                const equip = sh.equipment?.find(e => e.name === record.name && e.invoice_duration !== null);
                if (!equip) return null;
                const rawDuration = equip.invoice_duration;
                const effectiveDuration = equip.effective_duration || Math.max(record.minDuration || 2.0, rawDuration);
                return (
                  <div key={idx} style={{ fontSize: '11px', marginBottom: '2px' }}>
                    SH {idx + 1}: {rawDuration.toFixed(3)} hrs × {equip.invoice_quantity} = {effectiveDuration.toFixed(2)} hrs
                    {effectiveDuration > rawDuration && <span style={{ color: '#ffa940' }}> (min applied)</span>}
                  </div>
                );
              })}
              <div style={{ marginTop: '4px', borderTop: '1px solid #555', paddingTop: '4px' }}>
                Total: {record.effectiveDuration.toFixed(2)} hrs
              </div>
            </div>
          }
        >
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>
            {record.effectiveDuration.toFixed(2)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: (
        <span>
          Total Quantity{' '}
          <Tooltip title="Total number of pieces across all service histories for this equipment">
            <InfoCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (value, record) => (
        <Tooltip 
          title={
            <div>
              <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>Quantity Breakdown:</div>
              {invoice?.service_histories?.map((sh, idx) => {
                const equip = sh.equipment?.find(e => e.name === record.name && e.invoice_quantity !== null);
                if (!equip) return null;
                return (
                  <div key={idx} style={{ fontSize: '11px', marginBottom: '2px' }}>
                    SH {idx + 1}: {equip.invoice_quantity} pieces
                  </div>
                );
              })}
              <div style={{ marginTop: '4px', borderTop: '1px solid #555', paddingTop: '4px' }}>
                Total: {value} pieces
              </div>
            </div>
          }
        >
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>
            {value}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Rate ($/hr)',
      dataIndex: 'rate',
      key: 'rate',
      render: (value) => `$${parseFloat(value).toFixed(2)}`,
    },
    {
      title: (
        <span>
          Total ($){' '}
          <Tooltip title="Sum of (duration × rate × quantity) across all service histories for this equipment">
            <InfoCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'total',
      key: 'total',
      render: (value, record) => (
        <Tooltip 
          title={
            <div>
              <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>Total Calculation:</div>
              {invoice?.service_histories?.map((sh, idx) => {
                const equip = sh.equipment?.find(e => e.name === record.name && e.invoice_duration !== null);
                if (!equip) return null;
                const effectiveDuration = equip.effective_duration || Math.max(record.minDuration || 2.0, equip.invoice_duration);
                const lineTotal = effectiveDuration * record.rate * equip.invoice_quantity;
                return (
                  <div key={idx} style={{ fontSize: '11px', marginBottom: '2px' }}>
                    SH {idx + 1}: {effectiveDuration.toFixed(2)} hrs × ${record.rate.toFixed(2)}/hr × {equip.invoice_quantity} = ${lineTotal.toFixed(2)}
                  </div>
                );
              })}
              <div style={{ marginTop: '4px', borderTop: '1px solid #555', paddingTop: '4px' }}>
                Total: ${value.toFixed(2)}
              </div>
            </div>
          }
        >
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>
            ${value.toFixed(2)}
          </span>
        </Tooltip>
      ),
    },
  ];

  const handleContractTypeEdit = async (value) => {
    try {
      setEditingContractType(false);
      // Persist change to backend
      await updateInvoice(invoiceId, { contract_type: value });

      // Refresh invoice data from backend to ensure UI matches server state
      await fetchInvoice();

      // Update local contract type state immediately as well
      setContractType(value);

      // Notify parent list so the table row reflects the new contract type
      if (onContractTypeChange) {
        onContractTypeChange(invoiceId, value);
      }

      messageApi.success('Contract type updated successfully!');
    } catch (error) {
      console.error('Error updating contract type:', error);
      messageApi.error('Failed to update contract type.');
    }
  };

  useEffect(() => {
    fetchInvoice();
    fetchEquipment();
  }, [invoiceId]);

  if (loading || !invoice) {
    return <Typography>Loading...</Typography>;
  }

  const contractOptions =
  invoice.event_type === constants.event_type.snow
    ? [
        { label: constants.contract_type.accum, value: constants.contract_type.accum },
        { label: constants.contract_type.hourly, value: constants.contract_type.hourly },
        { label: constants.contract_type.per_push, value: constants.contract_type.per_push },
        { label: constants.contract_type.all_inclusive, value: constants.contract_type.all_inclusive },
      ]
    : [
        { label: constants.contract_type.per_visit, value: constants.contract_type.per_visit },
        { label: constants.contract_type.flat_rate, value: constants.contract_type.flat_rate },
        { label: constants.contract_type.all_inclusive, value: constants.contract_type.all_inclusive },
      ];

  return (
    <div>
      <Typography.Title level={3}>Edit Invoice</Typography.Title>
      <Divider />

      {/* Invoice Information */}
      <Descriptions title="Invoice Details" bordered column={1} labelStyle={{ width: '150px' }}>
        <Descriptions.Item label="Invoice ID">{invoice.invoice_id}</Descriptions.Item>
        <Descriptions.Item label="Client">{invoice.client_name}</Descriptions.Item>
        <Descriptions.Item label="Event">{invoice.event_name}</Descriptions.Item>
        <Descriptions.Item label="Contract Type">
          {editingContractType ? (
            <div ref={editingRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Select
                style={{ width: '200px' }}
                value={contractType}
                onChange={(value) => setContractType(value)}
                options={contractOptions}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Button
                  shape='circle'
                  icon={<CloseOutlined style={{ color: 'red' }} />}
                  onClick={() => {
                    setEditingContractType(false);
                    setContractType(invoice?.contract_type);
                  }}
                />
                <Button
                  shape='circle'
                  icon={<CheckOutlined style={{ color: 'green' }} />}
                  onClick={() => handleContractTypeEdit(contractType)}
                />
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography.Text>{contractType}</Typography.Text>
              <Button
                shape='circle'
                icon={<EditOutlined />}
                onClick={() => setEditingContractType(true)}
              />
            </div>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Total Duration">{invoice.total_duration} hours</Descriptions.Item>
        <Descriptions.Item label="Total Amount">${invoice.total_amount_sum.toFixed(2)}</Descriptions.Item>
      </Descriptions>

      {invoice.synced_contract_type && invoice.contract_type !== invoice.synced_contract_type && (
        <>
          <Divider />
          <Alert
            message="Contract Type Mismatch"
            description={`This invoice was last synced with contract type "${invoice.synced_contract_type}" but is currently set to "${invoice.contract_type}". Please resync the invoice to update the amount calculation and PDF with the new contract type.`}
            type="warning"
            showIcon
            icon={<WarningOutlined />}
          />
        </>
      )}

      <Divider />
      {invoice.service_histories && invoice.service_histories.length > 0 ? (
        <>

          {/* Invoice Summary Table */}
          <Typography.Title level={4}>Invoice Summary</Typography.Title>
          <Table
            columns={invoiceSummaryColumns}
            dataSource={calculateInvoiceSummary()}
            pagination={false}
            size="small"
            summary={(pageData) => {
              const total = pageData.reduce((sum, item) => sum + item.total, 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Typography.Text strong>Total</Typography.Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}></Table.Summary.Cell>
                  <Table.Summary.Cell index={2}></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}></Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    <Typography.Text strong>${total.toFixed(2)}</Typography.Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />

          <Divider />

          {/* Service Histories */}
          <Typography.Title level={4}>Service Histories</Typography.Title>
          <Tabs>
            {invoice.service_histories.map((shr) => (
              <TabPane
                key={shr.id}
                tab={
                  <div>
                    <span>{toLocalDateTimeString(shr.started_at)}</span>
                    <br/>
                    <span>{`— ${toLocalDateTimeString(shr.ended_at)}`}</span>
                  </div>
                }
              >
                <ServiceHistoryTab
                  shr={shr}
                  equipment={equipment}
                  messageApi={messageApi}
                  type={INVOICE}
                  onEquipmentChange={() => {
                    // Refresh invoice data when equipment changes
                    fetchInvoice();
                  }}
                />
              </TabPane>
            ))}
          </Tabs>
        </>
      ) : (
        <>
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            <Typography.Text>No service histories associated with this invoice.</Typography.Text>
          </div>
        </>
      )}
    </div>
  );
};

export default InvoiceEdit;